import { useCallback, useContext, useMemo } from 'react';

import {
  DATA_DISPLAY_KEYS,
  PortfolioManagementContext,
} from '../../../providers/portfolio-management';
import { RiskFiltersContext } from '../../../providers/risk-management/risk-filters-provider';
import { RiskManagementContext } from '../../../providers/risk-management/risk-management-provider';
import { IAccountOption } from '../../../providers/venue-provider';

type IUpdateFreezeSpotMap = {
  accountOption?: IAccountOption;
  riskType: string;
};

export const useFreezeSpotValues = () => {
  const {
    freezeSpotMapRef,
    spotOverrideValue,
    riskSettingsSelected,
    selectedDataDisplayOption,
    selectedAccountsAggregated,
  } = useContext(PortfolioManagementContext);
  const { getData } = useContext(RiskManagementContext);
  const {
    filters: { token, riskToken },
  } = useContext(RiskFiltersContext);

  const persistSpotValues = useCallback(
    (data: any): void => {
      if (!data) return;

      const currentValues = freezeSpotMapRef.current || {};

      const tokensObject: Record<string, number> = { ...currentValues };
      const spotValueIndex = 23;

      Object.entries(data).forEach(([token, tokenArray]) => {
        if (Array.isArray(tokenArray) && tokenArray.length > 0) {
          const spotValues = tokenArray[0]?.report?.spot;
          if (Array.isArray(spotValues) && spotValues.length > spotValueIndex) {
            if (!tokensObject.hasOwnProperty(token)) {
              tokensObject[token] = spotValues[spotValueIndex];
            }
          }
        }
      });

      freezeSpotMapRef.current = tokensObject;
    },
    [freezeSpotMapRef],
  );

  const updateSpotValues = useCallback(
    (token: string, newValue: number): void => {
      if (!freezeSpotMapRef.current) {
        freezeSpotMapRef.current = {};
      }

      if (Object.prototype.hasOwnProperty.call(freezeSpotMapRef.current, token)) {
        freezeSpotMapRef.current[token] = newValue;
      }
    },
    [freezeSpotMapRef],
  );

  const filterAccountsByExchanges = (accounts: any[], exchanges: string[]) => {
    return accounts.reduce((acc: any, account: any) => {
      const exchange = account.exchange;

      if (exchanges.includes(exchange)) {
        if (!acc[exchange]) {
          acc[exchange] = [];
        }
        acc[exchange].push(account);
      }
      return acc;
    }, {});
  };

  const aggregatedAccountsByExchange = useMemo(() => {
    return filterAccountsByExchanges(selectedAccountsAggregated, ['deribit', 'okx', 'otc']);
  }, [selectedAccountsAggregated]);

  const updateFreezeSpotMapAndCallGetData = ({ accountOption, riskType }: IUpdateFreezeSpotMap) => {
    if (spotOverrideValue === '') return;
    const spotValue = spotOverrideValue.replace(/,/g, '');
    const spotValueNumber = Number(spotValue).toFixed(2);
    const isPerAccount = selectedDataDisplayOption === DATA_DISPLAY_KEYS.perAccount;
    updateSpotValues(token, Number(spotValueNumber));

    if (isPerAccount) {
      getData({
        account: accountOption,
        dataParent: riskType,
        riskTokenParam: riskToken,
        riskSettings: riskSettingsSelected,
      });
    } else {
      Object.keys(aggregatedAccountsByExchange).forEach((exchange) => {
        const accounts = aggregatedAccountsByExchange[exchange];
        if (accounts.length > 0) {
          const isSpotCalculateAggregatedAccounts = true;

          getData({
            account: accounts,
            dataParent: riskType,
            riskTokenParam: riskToken,
            riskSettings: riskSettingsSelected,
            isSpotAggregatedAccounts: isSpotCalculateAggregatedAccounts,
            accountsAggregated: selectedAccountsAggregated,
          });
        }
      });
    }
  };

  return { persistSpotValues, updateFreezeSpotMapAndCallGetData };
};
